
import React, { useState } from 'react';
import { Zap, BarChart3, Link as LinkIcon, Lock, Settings2, EyeOff, Eye, Edit3, Check, Copy, Share2, QrCode, Download, RefreshCw } from 'lucide-react';
import type { PageType } from '../types';
import type { ToastType } from './ToastNotification';

interface HeroProps {
  onNavigate?: (page: PageType) => void;
  isLoggedIn: boolean;
  onOpenAuth: () => void;
  showToast: (message: string, type: ToastType) => void;
}

interface CreatedLinkData {
  original: string;
  alias: string;
  shortUrl: string;
}

const Hero: React.FC<HeroProps> = ({ onNavigate, isLoggedIn, onOpenAuth, showToast }) => {
  const [url, setUrl] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customAlias, setCustomAlias] = useState('');
  const [password, setPassword] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCustomAliasEnabled, setIsCustomAliasEnabled] = useState(false);
  const [isPasswordEnabled, setIsPasswordEnabled] = useState(false);
  
  // Loading & Success States
  const [isLoading, setIsLoading] = useState(false);
  const [createdLink, setCreatedLink] = useState<CreatedLinkData | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url) {
      showToast('لطفاً آدرس لینک را وارد کنید', 'error');
      return;
    }

    let finalUrl = url.trim();
    if (!/^https?:\/\//i.test(finalUrl)) {
        finalUrl = 'https://' + finalUrl;
    }

    if (!isLoggedIn) {
      showToast('برای کوتاه کردن لینک لطفاً ابتدا وارد شوید', 'info');
      onOpenAuth();
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        original: finalUrl,
        alias: isCustomAliasEnabled && customAlias ? customAlias : undefined,
        password: isPasswordEnabled && password ? password : undefined,
        isPrivate: isPrivate ? 1 : 0
      };

      const response = await fetch('https://api.f4st.ir/api/links', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include', // Important to send the auth cookie
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        const shortUrl = `F4ST.IR/${data.alias}`;
        setCreatedLink({
          original: data.original,
          alias: data.alias,
          shortUrl: shortUrl
        });
        showToast('لینک با موفقیت ساخته شد', 'success');
        
        // Generate QR Code immediately
        const size = '500x500';
        // Add protocol for QR generator so it scans correctly
        const fullShortUrl = `https://${shortUrl}`; 
        const qrData = encodeURIComponent(fullShortUrl);
        const apiEndpoint = `https://api.qrserver.com/v1/create-qr-code/?size=${size}&data=${qrData}&margin=20&bgcolor=ffffff`;
        setQrCodeUrl(apiEndpoint);

        // Send request to backend to log QR generation
        fetch('https://api.f4st.ir/api/tools/qr', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ url: fullShortUrl })
        }).catch(err => console.error('Error logging QR generation:', err));

      } else {
        throw new Error(data.detail || 'خطا در ساخت لینک');
      }

    } catch (error: any) {
      console.error(error);
      showToast(error.message || 'مشکلی پیش آمده است', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (createdLink) {
      navigator.clipboard.writeText(`https://${createdLink.shortUrl}`);
      setIsCopied(true);
      showToast('لینک کپی شد', 'success');
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleDownloadQr = async () => {
    if (!qrCodeUrl) return;
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `qrcode-${createdLink?.alias}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showToast('تصویر QR دانلود شد', 'success');
    } catch (error) {
      console.error('Download failed:', error);
      window.open(qrCodeUrl, '_blank');
    }
  };

  const resetForm = () => {
    setCreatedLink(null);
    setQrCodeUrl(null);
    setUrl('');
    setCustomAlias('');
    setPassword('');
    setIsCustomAliasEnabled(false);
    setIsPasswordEnabled(false);
    setIsPrivate(false);
  };

  return (
    <section className="relative pt-20 lg:pt-32 pb-28 lg:pb-48 lg:min-h-[600px] bg-gradient-to-br from-blue-600 to-violet-600 dark:from-blue-900 dark:to-violet-900 text-white transition-colors duration-300">
      <style>
        {`
          @keyframes move-forever {
            0% { transform: translate3d(-90px,0,0); }
            100% { transform: translate3d(85px,0,0); }
          }
          .parallax > use {
            animation: move-forever 25s cubic-bezier(.55,.5,.45,.5) infinite;
          }
          .parallax > use:nth-child(1) { animation-delay: -2s; animation-duration: 7s; }
          .parallax > use:nth-child(2) { animation-delay: -3s; animation-duration: 10s; }
          .parallax > use:nth-child(3) { animation-delay: -4s; animation-duration: 13s; }
          .parallax > use:nth-child(4) { animation-delay: -5s; animation-duration: 20s; }
        `}
      </style>
      
      {/* Wave Background Overlay */}
      <div className="absolute inset-0 z-0 opacity-10 pointer-events-none">
         {/* Static overlay pattern */}
      </div>

      {/* Stars for Dark Mode */}
      <div className="absolute inset-0 z-0 pointer-events-none hidden dark:block">
        {[...Array(20)].map((_, i) => (
           <div
             key={i}
             className="absolute bg-white rounded-full animate-twinkle shadow-[0_0_8px_rgba(255,255,255,0.8)]"
             style={{
               top: `${Math.random() * 100}%`,
               left: `${Math.random() * 100}%`,
               width: `${Math.random() * 2 + 1}px`,
               height: `${Math.random() * 2 + 1}px`,
               animationDelay: `${Math.random() * 5}s`,
               opacity: Math.random() * 0.7 + 0.3
             }}
           />
        ))}
      </div>

      <div className="container mx-auto px-4 lg:px-6 relative z-30">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start lg:items-start">
          {/* Text Content */}
          <div className="space-y-6 lg:space-y-8 text-center lg:text-right lg:pt-12">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-1.5 lg:px-6 lg:py-2 rounded-full border border-white/30 animate-pulse">
              <Zap size={16} className="text-yellow-300 fill-yellow-300 lg:w-[18px] lg:h-[18px]" />
              <span className="font-medium text-xs md:text-sm lg:text-base">سریع ترین سرویس کوتاه کننده لینک</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight lg:leading-tight">
              لینک‌های خود را با <span className="text-yellow-300 inline-block transform hover:scale-105 transition-transform cursor-default font-sans">فست</span> کوتاه کنید
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              با کوتاه کننده لینک <span className="font-bold">فست</span> می‌توانید لینک‌های طولانی را به لینک‌های کوتاه و دلخواه تبدیل کنید تا به راحتی در شبکه‌های اجتماعی به اشتراک بگذارید.
            </p>

            <div className="flex flex-wrap justify-center lg:justify-start gap-2 lg:gap-3">
              {[
                { icon: Zap, text: "کوتاه کردن فوری", color: "text-yellow-500" },
                { icon: BarChart3, text: "آمار دقیق بازدید", color: "text-green-500" }
              ].map((badge, idx) => (
                <div key={idx} className="bg-white/95 dark:bg-slate-800/95 text-slate-800 dark:text-white px-3 py-2 lg:px-4 lg:py-2.5 rounded-xl flex items-center gap-1.5 lg:gap-2 shadow-lg font-medium text-xs lg:text-sm transition-colors backdrop-blur-sm">
                  <badge.icon className={`${badge.color} w-3.5 h-3.5 lg:w-4 lg:h-4`} />
                  <span>{badge.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Interaction Card Wrapper */}
          <div className="relative mt-8 h-[450px] lg:mt-0 lg:h-[480px]">
            {/* Conditional Rendering: Form OR Success Card */}
            {createdLink ? (
              // SUCCESS CARD - REDESIGNED
              <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-5 rounded-[1.5rem] shadow-2xl border border-white/50 dark:border-slate-700 absolute top-0 left-0 w-full z-40 animate-in zoom-in-95 duration-300">
                
                {/* Compact Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-slate-800 dark:text-white text-lg font-bold flex items-center gap-2">
                    <div className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center">
                       <Check size={14} />
                    </div>
                    لینک کوتاه شما:
                  </h3>
                </div>

                {/* Short Link & Inline Copy Button */}
                <div className="w-full flex items-center justify-between gap-2 bg-slate-50 dark:bg-slate-950 p-2 pl-3 pr-2 rounded-xl border border-emerald-200 dark:border-emerald-900/30 shadow-sm mb-6" dir="ltr">
                  <a href={`https://${createdLink.shortUrl}`} target="_blank" rel="noreferrer" className="flex-1 font-mono text-lg font-bold text-emerald-600 dark:text-emerald-400 text-left truncate hover:underline">
                    {createdLink.shortUrl}
                  </a>
                  
                  <button 
                      onClick={handleCopyLink}
                      className={`flex items-center justify-center p-2 rounded-lg transition-all duration-200 ${
                        isCopied 
                        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 scale-105' 
                        : 'bg-white dark:bg-slate-900 text-emerald-600 dark:text-emerald-400 border border-slate-200 dark:border-slate-800 hover:bg-emerald-50'
                      }`}
                      title="کپی لینک"
                      aria-label="کپی لینک کوتاه"
                    >
                      {isCopied ? <Check size={18} /> : <Copy size={18} />}
                    </button>
                </div>

                {/* QR Code & Actions - Improved Layout */}
                {qrCodeUrl && (
                  <div className="flex items-center justify-center gap-4 sm:gap-6 pb-2">
                    
                    {/* Left: New Link (Small Icon Button) */}
                    <button 
                      onClick={resetForm}
                      className="group relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-all border border-transparent hover:border-violet-200 dark:hover:border-violet-700/50"
                      aria-label="ساخت لینک جدید"
                    >
                      <RefreshCw size={20} className="sm:w-6 sm:h-6" />
                      <span className="absolute -bottom-8 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">لینک جدید</span>
                    </button>

                    {/* Center: QR Code (Big & Beautiful) */}
                    <div className="relative bg-white p-2 sm:p-3 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-black/20 border border-slate-100 dark:border-slate-700">
                       <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48 sm:w-56 sm:h-56 object-contain rounded-lg" />
                    </div>

                    {/* Right: Download (Small Icon Button) */}
                    <button 
                      onClick={handleDownloadQr}
                      className="group relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-all border border-transparent hover:border-emerald-200 dark:hover:border-emerald-700/50"
                      aria-label="دانلود QR Code"
                    >
                      <Download size={20} className="sm:w-6 sm:h-6" />
                       <span className="absolute -bottom-8 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">دانلود QR</span>
                    </button>

                  </div>
                )}
                
              </div>
            ) : (
              // CREATE FORM
              <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-5 sm:p-8 rounded-[1.5rem] lg:rounded-3xl shadow-2xl border border-white/50 dark:border-slate-700 transform transition-all hover:scale-[1.01] hover:shadow-[0_20px_50px_rgba(0,0,0,0.3)] absolute top-0 left-0 w-full z-40">
                <div className="text-center mb-6">
                  <h3 className="text-slate-800 dark:text-white text-xl lg:text-2xl font-bold mb-2">لینک خود را کوتاه کنید</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs lg:text-sm">لینک طولانی خود را در کادر زیر وارد کنید</p>
                </div>

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div className="relative group">
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <LinkIcon className="text-gray-400 group-focus-within:text-violet-600 dark:group-focus-within:text-violet-400 transition-colors" size={20} />
                    </div>
                    <input
                      type="text"
                      placeholder="F4ST.IR"
                      dir="ltr"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="w-full pl-4 pr-12 py-4 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white placeholder-slate-400 outline-none focus:border-violet-500 dark:focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all font-sans text-left text-base shadow-inner"
                      disabled={isLoading}
                    />
                  </div>

                  {/* Advanced Options Toggle */}
                  <div className="flex justify-end">
                    <button 
                      type="button" 
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors font-bold bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700"
                      aria-label={showAdvanced ? 'بستن تنظیمات پیشرفته' : 'باز کردن تنظیمات پیشرفته'}
                      aria-expanded={showAdvanced}
                    >
                      <Settings2 size={14} />
                      <span>{showAdvanced ? 'بستن تنظیمات' : 'تنظیمات پیشرفته'}</span>
                    </button>
                  </div>

                  {/* Advanced Options Panel */}
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showAdvanced ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                      {/* Use Grid instead of Flex+Divide to prevent layout stretching issues */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-start">
                        
                        {/* Custom Alias Option */}
                        <div className="space-y-3">
                          <label htmlFor="custom-alias-toggle" className="flex items-center justify-between cursor-pointer p-1">
                            <span className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300">
                              <Edit3 size={14} />
                              <span>آدرس دلخواه</span>
                            </span>
                            <div className="relative">
                              <input id="custom-alias-toggle" type="checkbox" className="sr-only peer" checked={isCustomAliasEnabled} onChange={() => setIsCustomAliasEnabled(!isCustomAliasEnabled)} />
                              <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-violet-600"></div>
                            </div>
                          </label>
                          {isCustomAliasEnabled && (
                            <div className="flex items-center animate-in slide-in-from-top-1" dir="ltr">
                              <span className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-3 py-2.5 rounded-l-xl border-y border-l border-slate-200 dark:border-slate-700 text-sm font-bold font-sans">
                                F4ST.IR/
                              </span>
                              <input
                                type="text"
                                placeholder="alias"
                                value={customAlias}
                                onChange={(e) => setCustomAlias(e.target.value)}
                                className="flex-1 py-2.5 px-3 rounded-r-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white text-sm outline-none focus:border-violet-500 text-left font-sans"
                              />
                            </div>
                          )}
                        </div>

                        {/* Password Option */}
                        <div className="space-y-3">
                          <label htmlFor="password-toggle" className="flex items-center justify-between cursor-pointer p-1">
                            <span className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300">
                              <Lock size={14} />
                              <span>لینک رمزدار</span>
                            </span>
                            <div className="relative">
                              <input id="password-toggle" type="checkbox" className="sr-only peer" checked={isPasswordEnabled} onChange={() => setIsPasswordEnabled(!isPasswordEnabled)} />
                              <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-violet-600"></div>
                            </div>
                          </label>
                          {isPasswordEnabled && (
                            <div className="relative animate-in slide-in-from-top-1">
                              <input
                                type={showPassword ? "text" : "password"}
                                dir="ltr" // Added dir="ltr" to fix cursor jumping
                                placeholder="رمز عبور"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-3 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-white text-sm outline-none focus:border-violet-500 text-left font-sans"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                              >
                                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Private Stats Toggle */}
                        <div className="space-y-3">
                          <label htmlFor="private-toggle" className="flex items-center justify-between cursor-pointer p-1">
                            <span className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-300">
                              <EyeOff size={14} />
                              <span>آمار خصوصی</span>
                            </span>
                            <div className="relative">
                              <input id="private-toggle" type="checkbox" className="sr-only peer" checked={isPrivate} onChange={() => setIsPrivate(!isPrivate)} />
                              <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-violet-600"></div>
                            </div>
                          </label>
                        </div>

                      </div>
                    </div>
                  </div>


                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white py-4 rounded-2xl font-bold text-lg shadow-lg shadow-violet-200 dark:shadow-none hover:shadow-xl hover:shadow-violet-300 hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group relative overflow-hidden disabled:opacity-80 disabled:cursor-not-allowed"
                  >
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 rounded-2xl"></div>
                    {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <span className="relative flex items-center gap-2">
                        {!isLoggedIn && <Lock className="w-5 h-5 opacity-80" />}
                        {isLoggedIn ? <Zap className="group-hover:fill-white transition-colors w-5 h-5" /> : null}
                        <span>کوتاه کن</span>
                      </span>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-[10px] lg:text-xs text-slate-400 dark:text-slate-500">
                    با استفاده از سرویس، با <button onClick={() => onNavigate && onNavigate('terms')} className="text-violet-600 dark:text-violet-400 hover:underline bg-transparent border-none p-0 inline font-inherit cursor-pointer">قوانین و مقررات</button> فست موافقت می‌کنید.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animated Wave Bottom */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-20">
        <svg
          className="relative block w-[calc(100%+1.3px)] h-[70px] md:h-[120px]"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shapeRendering="auto"
        >
          <defs>
            <path
              id="gentle-wave"
              d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z"
            />
          </defs>
          <g className="parallax">
            {/* Layer 1: Violet - Deepest */}
            <use xlinkHref="#gentle-wave" x="48" y="0" className="fill-violet-500/30 dark:fill-violet-900/40" />
            {/* Layer 2: Blue - Middle */}
            <use xlinkHref="#gentle-wave" x="48" y="3" className="fill-blue-500/40 dark:fill-blue-900/50" />
            {/* Layer 3: Indigo - Front Transparent */}
            <use xlinkHref="#gentle-wave" x="48" y="5" className="fill-indigo-400/50 dark:fill-indigo-800/60" />
            {/* Layer 4: Solid Background */}
            <use xlinkHref="#gentle-wave" x="48" y="7" className="fill-blue-50 dark:fill-slate-900" />
          </g>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
